import {type Client} from '@atproto/lex'
import {type NsidString} from '@atproto/syntax'

import {logger} from '#/ageAssurance/logger'
import {BRAND} from '#/config/brand'
import {com} from '#/lexicons'

/**
 * Client for the waldmeister age-assurance backend (`waldmeister-age-service`).
 *
 * The app sources the user's *declared* age from here instead of app.bsky
 * preferences, which OAuth sessions can't read/write. Auth is an atproto
 * service-auth JWT minted per call via `com.atproto.server.getServiceAuth`,
 * which works identically on OAuth and app-password sessions. Only boolean
 * threshold flags are sent/stored - never the birthdate.
 */

export type WaldmeisterAgeFlags = {
  over13: boolean
  over16: boolean
  over18: boolean
}
export type WaldmeisterAgeStatus = {
  declared: boolean
} & Partial<WaldmeisterAgeFlags>

const GET_STATUS = 'eu.waldmeister.age.getStatus'
const SET_STATUS = 'eu.waldmeister.age.setStatus'

/**
 * Rebuild a representative birthdate string from stored flags for the region
 * rule engine (which compares against ages 13/16/18). We never store the real
 * date of birth, so we synthesize one at the highest passed threshold;
 * "declared but under 13" maps to a young sentinel so the global under-13 block
 * triggers.
 */
export function birthdateFromFlags(flags: WaldmeisterAgeFlags): string {
  const age = flags.over18 ? 18 : flags.over16 ? 16 : flags.over13 ? 13 : 5
  const today = new Date()
  return new Date(
    today.getFullYear() - age,
    today.getMonth(),
    today.getDate() - 1, // day before, so the age is definitely reached
  ).toISOString()
}

/**
 * We intentionally do NOT pass `exp`. The PDS then derives both `iat` and `exp`
 * from its own clock (a short default lifetime), so the token is immune to
 * client clock skew. Deriving `exp` from the local clock was fragile in both
 * directions: a client running behind real time minted an already-expired
 * token (rejected by the PDS as a past `exp`), while a client running ahead
 * minted a long-lived token that the age service rejected with "jwt exceeds
 * maximum age (300s)". A PDS-clock-relative short token avoids both, since
 * `bearer` is called immediately before each request.
 */
async function bearer(client: Client, lxm: NsidString): Promise<string> {
  const data = await client.call(com.atproto.server.getServiceAuth, {
    aud: BRAND.ageAssurance.serviceDid,
    lxm,
  })
  return `Bearer ${data.token}`
}

/**
 * Time budget for a single backend call. The gate fails closed, so if the
 * service hangs we want to fail fast to the gate rather than block the app boot
 * decision (or the declaration spinner) indefinitely.
 */
const REQUEST_TIMEOUT = 8000

/**
 * Cross-platform request-timeout signal. `AbortSignal.timeout()` is a
 * browser/Node API that Hermes (React Native) does not implement, so calling it
 * throws synchronously on native - which made every age-service call fail and
 * silently failed the gate open to a restricted state (web, which has the API,
 * worked fine). `AbortController` + `setTimeout` is supported on both.
 */
function timeoutSignal(ms: number): AbortSignal {
  const controller = new AbortController()
  setTimeout(() => controller.abort(), ms)
  return controller.signal
}

export async function getWaldmeisterAgeStatus(
  client: Client,
): Promise<WaldmeisterAgeStatus> {
  const authorization = await bearer(client, GET_STATUS)
  const res = await fetch(
    `${BRAND.ageAssurance.serviceUrl}/xrpc/${GET_STATUS}`,
    {headers: {authorization}, signal: timeoutSignal(REQUEST_TIMEOUT)},
  )
  if (!res.ok) {
    throw new Error(`getWaldmeisterAgeStatus: ${res.status}`)
  }
  return (await res.json()) as WaldmeisterAgeStatus
}

export async function setWaldmeisterAgeStatus(
  client: Client,
  flags: WaldmeisterAgeFlags,
): Promise<void> {
  const authorization = await bearer(client, SET_STATUS)
  const res = await fetch(
    `${BRAND.ageAssurance.serviceUrl}/xrpc/${SET_STATUS}`,
    {
      method: 'POST',
      headers: {authorization, 'content-type': 'application/json'},
      body: JSON.stringify(flags),
      signal: timeoutSignal(REQUEST_TIMEOUT),
    },
  )
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    logger.error('setWaldmeisterAgeStatus failed', {
      safeMessage: `${res.status} ${text}`,
    })
    throw new Error(`setWaldmeisterAgeStatus: ${res.status}`)
  }
}
